import { createContext, useContext } from 'react';
import type { PartPriceSource } from '@/hooks/usePartPricing';
import type { AnnotatedBillingLine } from './billingLineSource';

/** What a stock-consumed part line shows and offers about its price (#3034). */
export interface ConsumedPartPriceInfo {
  /** `null`: a legacy line recorded before price sources were kept. Its origin is unknown, never catalog. */
  priceSource: PartPriceSource | null;
  /** An explicit price lookup against the current rules is available for this line (catalog part, caller may). */
  canReprice: boolean;
}

export interface BillingLineActions {
  resetFixedServicePrice: (line: AnnotatedBillingLine) => void;
  /**
   * Take the user to the service's steps in the Works card, where the hours
   * and discounts of a combined labour line are edited (#2638). Absent when the
   * host page has no Works card to open.
   */
  openServiceLabor?: ((bookingServiceId: string) => void) | undefined;
  /** The price source of a stock-consumed part line, by its work_order_parts id; absent when the host has none. */
  consumedPartPrice?: ((consumedPartId: string) => ConsumedPartPriceInfo | undefined) | undefined;
  /** Asks to look the price of a consumed catalog part up again. The host confirms with the user before anything is sent. */
  requestReprice?: ((line: AnnotatedBillingLine) => void) | undefined;
}

const noopActions: BillingLineActions = {
  resetFixedServicePrice: () => undefined,
};

export const BillingLineActionsContext = createContext<BillingLineActions>(noopActions);

export function useBillingLineActions(): BillingLineActions {
  return useContext(BillingLineActionsContext);
}
